/**
 * Lite Activities API Client
 * 處理活動（購買清單）的 API 調用
 */

import { getApiBase } from './apiUtils.js';
import { handleLiteErrorResponse } from './liteApiError.js';

/** 單一商品項目（API） */
export interface ActivityProductItemSimple {
  id: string;
  name: string;
  price: number;
  quantity?: number;
}

/** 多規格項目（API，共用總量） */
export interface ActivityProductItemVariant {
  id: string;
  name: string;
  maxTotalQuantity: number;
  specs: Array<{ id: string; name: string; price: number }>;
}

/** 活動商品項目：單一或多規格 */
export type ActivityProductItem =
  | ActivityProductItemSimple
  | ActivityProductItemVariant;

/**
 * 建立活動請求
 */
export interface CreateActivityRequest {
  title: string;
  description?: string | null;
  createdBy?: string | null;
  status?: 'draft' | 'active' | 'inactive';
  startDate?: string | null;
  endDate?: string | null;
  productList?: ActivityProductItem[];
  autoPublish?: boolean; // 自動發佈
  autoSettle?: boolean; // 自動結算
}

/**
 * 更新活動請求
 */
export interface UpdateActivityRequest {
  title?: string;
  description?: string | null;
  status?: 'draft' | 'active' | 'inactive';
  startDate?: string | null;
  endDate?: string | null;
  productList?: ActivityProductItem[];
  autoPublish?: boolean; // 自動發佈
  autoSettle?: boolean; // 自動結算
}

/**
 * 活動資料
 */
export interface Activity {
  id: string;
  groupId: string | null; // LINE 群組 ID（Square 脈絡為 null）
  title: string;
  description: string | null;
  createdBy: string | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  productList?: Array<
    (ActivityProductItemSimple | ActivityProductItemVariant) & {
      totalQuantity?: number;
      waitlistNumber?: number;
    }
  >;
  autoPublish?: boolean; // 自動發佈
  autoSettle?: boolean; // 自動結算
  createdAt: string;
  updatedAt: string;
  /** 活動報名短網址（後端 /g/:shortCode），分享時一律使用 */
  shortUrl?: string | null;
}

/** 發佈時 API 回傳的分享內容（用戶僅能分享純文字） */
export interface PublishShare {
  sharePath: string;
  /** 縮網址（後端 /g/:shortCode），發佈時產生；有則優先使用於分享連結 */
  shortUrl?: string;
  /** 發佈後分享到 LINE 的純文字（依自訂標題/內文與 {activityTitle}、{joinUrl} 替換） */
  shareMessageText: string;
}

export interface CreateActivityOptions {
  /** Square 情境：傳入 6 碼 suffix，後端依此解析 square_id */
  squareSuffix?: string;
}

/**
 * 建立新活動
 */
export async function createActivity(
  request: CreateActivityRequest,
  jwtToken: string,
  options?: CreateActivityOptions
): Promise<Activity> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  console.log('📤 建立新活動:', request);

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify(request),
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '建立活動失敗，請稍後再試',
    });
  }

  const result = await response.json();
  console.log('✅ 建立活動成功:', result);
  return result.data;
}

/** 菜單辨識草稿（不寫入 DB，供預填表單） */
export interface RecognizeMenuDraft {
  title: string | null;
  description: string | null;
  productList: ActivityProductItem[];
}

/**
 * 上傳菜單圖片，AI 辨識產生活動草稿（可多張，會合併成一份）
 */
export async function recognizeMenuImages(
  files: File[],
  jwtToken: string
): Promise<RecognizeMenuDraft> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  if (files.length === 0) {
    throw new Error('請至少選擇一張菜單圖片');
  }

  const formData = new FormData();
  for (const file of files) {
    formData.append('images', file);
  }

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/recognize-image`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jwtToken}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '菜單辨識失敗，請稍後再試',
    });
  }

  const result = await response.json();
  if (!result.success || !result.data) {
    throw new Error('辨識回傳格式錯誤');
  }
  return result.data;
}

/**
 * 更新活動
 */
export async function updateActivity(
  activityId: string,
  request: UpdateActivityRequest,
  jwtToken: string,
  options?: LiteSquareContext
): Promise<Activity & { publishShare?: PublishShare }> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  console.log('📤 更新活動:', { activityId, request });

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/${activityId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify(request),
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '更新活動失敗，請稍後再試',
    });
  }

  const result = await response.json();
  console.log('✅ 更新活動成功:', result);
  return result.data;
}

/**
 * 取得單一活動
 */
export async function getActivity(
  activityId: string,
  jwtToken: string,
  options?: LiteSquareContext
): Promise<Activity> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  console.log('📤 取得活動:', activityId);

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/${activityId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '取得活動失敗，請稍後再試',
    });
  }

  const result = await response.json();
  console.log('✅ 取得活動成功:', result);
  return result.data;
}

/** 呼叫 Lite API 時可帶的 Square 情境（與 createActivity 一致） */
export interface LiteSquareContext {
  squareSuffix?: string;
}

/**
 * 取得活動列表
 */
export async function getActivities(
  jwtToken: string,
  options?: {
    page?: number;
    limit?: number;
    squareSuffix?: string;
  }
): Promise<{
  activities: Activity[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  const params = new URLSearchParams();
  if (options?.page) {
    params.set('page', String(options.page));
  }
  if (options?.limit) {
    params.set('limit', String(options.limit));
  }

  const url = `${apiBase}/api/lite/activities${params.toString() ? `?${params.toString()}` : ''}`;
  const timestamp = new Date().toISOString();
  console.log(
    `[liteActivitiesClient] [${timestamp}] 📤 取得活動列表 - 請求開始`
  );
  console.log(`[liteActivitiesClient] [${timestamp}] 請求 URL:`, url);
  console.log(`[liteActivitiesClient] [${timestamp}] 請求參數:`, {
    page: options?.page,
    limit: options?.limit,
    jwtTokenLength: jwtToken.length,
    jwtTokenPrefix: jwtToken.substring(0, 20) + '...',
  });

  const requestStartTime = Date.now();
  const response = await liteApiFetch(
    url,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );
  const requestEndTime = Date.now();

  console.log(
    `[liteActivitiesClient] [${timestamp}] 📥 API 回應接收 (耗時 ${requestEndTime - requestStartTime}ms):`,
    {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
    }
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '取得活動列表失敗，請稍後再試',
    });
  }

  const result = await response.json();
  console.log(`[liteActivitiesClient] [${timestamp}] ✅ 取得活動列表成功:`, {
    activitiesCount: result.data?.length || 0,
    pagination: result.pagination,
    activities:
      result.data?.map((a: Activity) => ({
        id: a.id,
        title: a.title,
        status: a.status,
        groupId: a.groupId,
      })) || [],
    fullResponse: result,
  });
  return {
    activities: result.data || [],
    pagination: result.pagination || {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  };
}

/**
 * 刪除活動（軟刪除）
 */
export async function deleteActivity(
  activityId: string,
  jwtToken: string,
  options?: LiteSquareContext
): Promise<void> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  console.log('📤 刪除活動:', activityId);

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/${activityId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '刪除活動失敗，請稍後再試',
    });
  }

  const result = await response.json();
  console.log('✅ 刪除活動成功:', result);
}

/**
 * 結束活動
 */
export async function settleActivity(
  activityId: string,
  jwtToken: string,
  options?: LiteSquareContext
): Promise<{ id: string; status: string }> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  console.log('📤 結束活動:', activityId);

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/${activityId}/settle`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '結束活動失敗，請稍後再試',
    });
  }

  const result = await response.json();
  console.log('✅ 結束活動成功:', result);
  return result.data;
}

/**
 * 活動結果
 */
export interface ActivityResult {
  shopType?: string | null;
  activity: {
    id: string;
    title: string;
    description: string | null;
    status: string;
    startDate: string | null;
    endDate: string | null;
    productList: Array<{
      id: string;
      name: string;
      price: number;
      quantity?: number;
    }>;
  };
  productSummary: Record<
    string,
    {
      totalQuantity: number;
    }
  >;
  participants: Array<{
    userId: string;
    userName: string | null;
    userLineUserId: string;
    items: Array<{
      itemId: string;
      productKey: string;
      quantity: number;
      delivered: boolean;
      cancelled: boolean;
    }>;
    firstParticipatedAt: string;
    remarks: string | null;
  }>;
}

/**
 * 取得活動結果
 */
export async function getActivityResult(
  activityId: string,
  jwtToken: string,
  options?: LiteSquareContext
): Promise<ActivityResult> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  console.log('📤 取得活動結果:', activityId);

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/${activityId}/result`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '取得活動結果失敗，請稍後再試',
    });
  }

  const result = await response.json();
  console.log('✅ 取得活動結果成功:', result);
  return result.data;
}

/**
 * 取得指定用戶在指定活動所屬商家的預繳資訊
 */
export async function getPrepaidInfo(
  activityId: string,
  userId: string,
  jwtToken: string,
  options?: LiteSquareContext
): Promise<{
  remainingCount: number;
  gender: 'male' | 'female' | 'other' | null;
  nickname: string | null;
}> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/${activityId}/prepaid/${userId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '取得預繳資訊失敗，請稍後再試',
    });
  }

  const result = await response.json();
  return result.data;
}

/**
 * 更新（增加）指定用戶的預繳次數與性別／暱稱
 */
export async function upsertPrepaidInfo(
  activityId: string,
  userId: string,
  payload: {
    addCount: number;
    gender: 'male' | 'female' | 'other' | null;
    nickname: string | null;
  },
  jwtToken: string,
  options?: LiteSquareContext
): Promise<{
  remainingCount: number;
  gender: 'male' | 'female' | 'other' | null;
  nickname: string | null;
}> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/${activityId}/prepaid/${userId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify(payload),
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '更新預繳資訊失敗，請稍後再試',
    });
  }

  const result = await response.json();
  return result.data;
}

/**
 * 更新交付/棄單狀態
 */
export async function updateDeliveryStatus(
  activityId: string,
  itemId: string,
  delivered?: boolean,
  cancelled?: boolean,
  jwtToken?: string,
  options?: LiteSquareContext
): Promise<{ id: string; delivered: boolean; cancelled: boolean }> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  if (!jwtToken) {
    throw new Error('需要 JWT Token');
  }

  console.log('📤 更新交付/棄單狀態:', {
    activityId,
    itemId,
    delivered,
    cancelled,
  });

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/${activityId}/items/${itemId}/delivery`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({
        delivered,
        cancelled,
      }),
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '更新狀態失敗，請稍後再試',
    });
  }

  const result = await response.json();
  console.log('✅ 更新狀態成功:', result);
  return result.data;
}

/**
 * 取得活動統計
 */
export async function getActivityStatistics(
  activityId: string,
  jwtToken: string,
  options?: LiteSquareContext
): Promise<{
  delivered: number;
  undelivered: number;
  cancelled: number;
  total: number;
  allHaveStatus: boolean;
}> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  console.log('📤 取得活動統計:', activityId);

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/${activityId}/statistics`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '取得活動統計失敗，請稍後再試',
    });
  }

  const result = await response.json();
  console.log('✅ 取得活動統計成功:', result);
  return result.data;
}

/**
 * 移除活動（軟刪除）
 */
export async function removeActivity(
  activityId: string,
  jwtToken: string,
  options?: LiteSquareContext
): Promise<void> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  console.log('📤 移除活動:', activityId);

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/${activityId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    let errorMessage = '移除活動失敗，請稍後再試';
    try {
      const error = await response.json();
      console.error('❌ 移除活動失敗:', error);
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      console.error(
        '❌ 移除活動失敗（無法解析錯誤）:',
        response.status,
        response.statusText
      );
      errorMessage = `移除活動失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  console.log('✅ 移除活動成功');
}

/**
 * 複製活動
 */
export async function duplicateActivity(
  activityId: string,
  jwtToken: string,
  options?: LiteSquareContext
): Promise<Activity> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  console.log('📤 複製活動:', activityId);

  const response = await liteApiFetch(
    `${apiBase}/api/lite/activities/${activityId}/duplicate`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
    options?.squareSuffix ? { squareSuffix: options.squareSuffix } : undefined
  );

  if (!response.ok) {
    let errorMessage = '複製活動失敗，請稍後再試';
    try {
      const error = await response.json();
      console.error('❌ 複製活動失敗:', error);
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      console.error(
        '❌ 複製活動失敗（無法解析錯誤）:',
        response.status,
        response.statusText
      );
      errorMessage = `複製活動失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  const result = await response.json();
  console.log('✅ 複製活動成功:', result);
  return result.data;
}
